import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Send, Zap, Users, FileText, Settings,
  Plus, Search, Edit2, Trash2, Play, Pause, Eye,
  TrendingUp, BarChart3, Target, MousePointer,
  Save, X, Check, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const MarketingPanel = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [lists, setLists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Modal states
  const [showSubscriberModal, setShowSubscriberModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [subscriberForm, setSubscriberForm] = useState({ email: '', first_name: '', last_name: '', lists: [], tags: '' });
  const [listForm, setListForm] = useState({ name: '', description: '' });
  const [campaignForm, setCampaignForm] = useState({ name: '', subject: '', from_name: '', from_email: '', html_content: '' });
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', html_content: '', category: 'marketing' });
  
  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'campaigns', name: 'Campaigns', icon: Send },
    { id: 'automations', name: 'Automations', icon: Zap },
    { id: 'subscribers', name: 'Subscribers', icon: Users },
    { id: 'lists', name: 'Lists', icon: Target },
    { id: 'templates', name: 'Templates', icon: FileText },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  const statusColors = {
    draft: 'bg-zinc-500/20 text-zinc-400',
    scheduled: 'bg-blue-500/20 text-blue-400',
    sending: 'bg-yellow-500/20 text-yellow-400',
    sent: 'bg-green-500/20 text-green-400',
    paused: 'bg-orange-500/20 text-orange-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    subscribed: 'bg-green-500/20 text-green-400',
    unsubscribed: 'bg-red-500/20 text-red-400',
    bounced: 'bg-orange-500/20 text-orange-400'
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, subscribersRes, listsRes, campaignsRes, automationsRes, templatesRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/websites/${websiteId}/marketing-stats`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/subscribers`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/subscriber-lists`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/campaigns`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/automations`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/email-templates`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/marketing-settings`, { headers }),
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (subscribersRes.ok) setSubscribers(await subscribersRes.json());
      if (listsRes.ok) setLists(await listsRes.json());
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (automationsRes.ok) setAutomations(await automationsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load marketing data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  // Subscriber CRUD
  const saveSubscriber = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...subscriberForm,
          tags: subscriberForm.tags ? subscriberForm.tags.split(',').map(t => t.trim()) : []
        })
      });
      if (response.ok) {
        toast.success('Subscriber added!');
        setShowSubscriberModal(false);
        setSubscriberForm({ email: '', first_name: '', last_name: '', lists: [], tags: '' });
        fetchAllData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add subscriber');
      }
    } catch (error) {
      toast.error('Failed to add subscriber');
    }
  };

  const deleteSubscriber = async (id) => {
    if (!confirm('Delete this subscriber?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/subscribers/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Subscriber deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // List CRUD
  const saveList = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/subscriber-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(listForm)
      });
      if (response.ok) {
        toast.success('List created!');
        setShowListModal(false);
        setListForm({ name: '', description: '' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  // Campaign CRUD
  const saveCampaign = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(campaignForm)
      });
      if (response.ok) {
        toast.success('Campaign created!');
        setShowCampaignModal(false);
        setCampaignForm({ name: '', subject: '', from_name: '', from_email: '', html_content: '' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create campaign');
    }
  };

  // Template CRUD
  const saveTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/email-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(templateForm)
      });
      if (response.ok) {
        toast.success('Template created!');
        setShowTemplateModal(false);
        setTemplateForm({ name: '', subject: '', html_content: '', category: 'marketing' });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  // Settings
  const saveSettings = async () => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/marketing-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Total Subscribers</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_subscribers || 0}</p>
            </div>
            <Users className="w-10 h-10 text-amber-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl p-6 border border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Active Subscribers</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.active_subscribers || 0}</p>
            </div>
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Campaigns Sent</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.sent_campaigns || 0}</p>
            </div>
            <Send className="w-10 h-10 text-blue-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl p-6 border border-violet-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Active Automations</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.active_automations || 0}</p>
            </div>
            <Zap className="w-10 h-10 text-violet-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Campaigns</h3>
          <div className="space-y-3">
            {(stats?.recent_campaigns || []).slice(0, 5).map(campaign => (
              <div key={campaign.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium">{campaign.name}</p>
                  <p className="text-sm text-zinc-400">{campaign.subject}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${statusColors[campaign.status]}`}>
                  {campaign.status}
                </span>
              </div>
            ))}
            {(!stats?.recent_campaigns || stats.recent_campaigns.length === 0) && (
              <p className="text-center text-zinc-500 py-8">No campaigns yet</p>
            )}
          </div>
        </div>

        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setShowCampaignModal(true)} className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors">
              <Send className="w-5 h-5 text-amber-400" />
              <span>New Campaign</span>
            </button>
            <button onClick={() => setShowSubscriberModal(true)} className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors">
              <Users className="w-5 h-5 text-emerald-400" />
              <span>Add Subscriber</span>
            </button>
            <button onClick={() => setShowListModal(true)} className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors">
              <Target className="w-5 h-5 text-blue-400" />
              <span>Create List</span>
            </button>
            <button onClick={() => setShowTemplateModal(true)} className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors">
              <FileText className="w-5 h-5 text-violet-400" />
              <span>New Template</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCampaigns = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
        </select>
        <Button onClick={() => setShowCampaignModal(true)} className="bg-amber-500">
          <Plus className="w-4 h-4 mr-2" />New Campaign
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Campaign</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Sent</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Opens</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Clicks</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {campaigns.map(campaign => (
              <tr key={campaign.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <p className="font-medium">{campaign.name}</p>
                  <p className="text-sm text-zinc-400">{campaign.subject}</p>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${statusColors[campaign.status]}`}>
                    {campaign.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-zinc-400">{campaign.sent_count || 0}</td>
                <td className="px-6 py-4">
                  <span className="text-zinc-400">{campaign.unique_opens || 0}</span>
                  <span className="text-xs text-zinc-500 ml-1">({campaign.open_rate?.toFixed(1) || 0}%)</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-zinc-400">{campaign.unique_clicks || 0}</span>
                  <span className="text-xs text-zinc-500 ml-1">({campaign.click_rate?.toFixed(1) || 0}%)</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button className="p-2 hover:bg-zinc-700 rounded-lg"><Eye className="w-4 h-4 text-zinc-400" /></button>
                    <button className="p-2 hover:bg-zinc-700 rounded-lg"><Edit2 className="w-4 h-4 text-zinc-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {campaigns.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Send className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No campaigns yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAutomations = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Email Automations</h3>
        <Button className="bg-amber-500">
          <Plus className="w-4 h-4 mr-2" />New Automation
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {automations.map(automation => (
          <div key={automation.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <Zap className="w-8 h-8 text-amber-400" />
              <span className={`px-2 py-1 rounded-full text-xs ${statusColors[automation.status]}`}>
                {automation.status}
              </span>
            </div>
            <h4 className="font-semibold mb-2">{automation.name}</h4>
            <p className="text-sm text-zinc-400 mb-4">{automation.description || 'No description'}</p>
            <div className="flex items-center justify-between text-sm text-zinc-500">
              <span>{automation.entered_count || 0} entered</span>
              <span>{automation.completed_count || 0} completed</span>
            </div>
          </div>
        ))}
        {automations.length === 0 && (
          <div className="col-span-2 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No automations yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSubscribers = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input type="text" placeholder="Search subscribers..." className="pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white w-64" />
          </div>
          <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
            <option value="">All Status</option>
            <option value="subscribed">Subscribed</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </div>
        <Button onClick={() => setShowSubscriberModal(true)} className="bg-amber-500">
          <Plus className="w-4 h-4 mr-2" />Add Subscriber
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Subscriber</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Lists</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Opens</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Clicks</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {subscribers.map(sub => (
              <tr key={sub.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <p className="font-medium">{sub.first_name} {sub.last_name}</p>
                  <p className="text-sm text-zinc-400">{sub.email}</p>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${statusColors[sub.status]}`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-zinc-400">{sub.lists?.length || 0} lists</td>
                <td className="px-6 py-4 text-zinc-400">{sub.opens_count || 0}</td>
                <td className="px-6 py-4 text-zinc-400">{sub.clicks_count || 0}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => deleteSubscriber(sub.id)} className="p-2 hover:bg-red-500/20 rounded-lg">
                    <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscribers.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No subscribers yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderLists = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Subscriber Lists</h3>
        <Button onClick={() => setShowListModal(true)} className="bg-amber-500">
          <Plus className="w-4 h-4 mr-2" />Create List
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {lists.map(list => (
          <div key={list.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <Target className="w-8 h-8 text-blue-400" />
              <span className="text-sm text-zinc-400">{list.subscriber_count || 0} subscribers</span>
            </div>
            <h4 className="font-semibold mb-2">{list.name}</h4>
            <p className="text-sm text-zinc-400">{list.description || 'No description'}</p>
          </div>
        ))}
        {lists.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No lists yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Email Templates</h3>
        <Button onClick={() => setShowTemplateModal(true)} className="bg-amber-500">
          <Plus className="w-4 h-4 mr-2" />New Template
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {templates.map(template => (
          <div key={template.id} className="bg-zinc-900 rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-violet-400" />
              <span className="text-xs bg-zinc-800 px-2 py-1 rounded">{template.category}</span>
            </div>
            <h4 className="font-semibold mb-2">{template.name}</h4>
            <p className="text-sm text-zinc-400 line-clamp-2">{template.subject || 'No subject'}</p>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No templates yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold mb-6">Email Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Default From Name</label>
            <input type="text" value={settings.from_name || ''} onChange={(e) => setSettings({...settings, from_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Your Company" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Default From Email</label>
            <input type="email" value={settings.from_email || ''} onChange={(e) => setSettings({...settings, from_email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="hello@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Reply-To Email</label>
            <input type="email" value={settings.reply_to || ''} onChange={(e) => setSettings({...settings, reply_to: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="support@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Company Name</label>
            <input type="text" value={settings.company_name || ''} onChange={(e) => setSettings({...settings, company_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Your Company Inc." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Company Address (for email footer)</label>
            <textarea value={settings.company_address || ''} onChange={(e) => setSettings({...settings, company_address: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={2} placeholder="123 Main St, City, Country" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="track_opens" checked={settings.track_opens !== false} onChange={(e) => setSettings({...settings, track_opens: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="track_opens">Track email opens</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="track_clicks" checked={settings.track_clicks !== false} onChange={(e) => setSettings({...settings, track_clicks: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="track_clicks">Track link clicks</label>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="double_optin" checked={settings.double_optin || false} onChange={(e) => setSettings({...settings, double_optin: e.target.checked})} className="w-5 h-5 rounded" />
            <label htmlFor="double_optin">Require double opt-in for new subscribers</label>
          </div>
          <div className="pt-4">
            <Button onClick={saveSettings} className="bg-amber-500">
              <Save className="w-4 h-4 mr-2" />Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'campaigns': return renderCampaigns();
      case 'automations': return renderAutomations();
      case 'subscribers': return renderSubscribers();
      case 'lists': return renderLists();
      case 'templates': return renderTemplates();
      case 'settings': return renderSettings();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Editor
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-400" />
            <h1 className="text-lg font-semibold text-white">Marketing & Campaigns</h1>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 min-h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-amber-500/20 text-amber-400' 
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">{renderContent()}</div>
      </div>

      {/* Subscriber Modal */}
      {showSubscriberModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Add Subscriber</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input type="email" value={subscriberForm.email} onChange={(e) => setSubscriberForm({...subscriberForm, email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="john@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Name</label>
                  <input type="text" value={subscriberForm.first_name} onChange={(e) => setSubscriberForm({...subscriberForm, first_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="John" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Last Name</label>
                  <input type="text" value={subscriberForm.last_name} onChange={(e) => setSubscriberForm({...subscriberForm, last_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Doe" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
                <input type="text" value={subscriberForm.tags} onChange={(e) => setSubscriberForm({...subscriberForm, tags: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="newsletter, premium" />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowSubscriberModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveSubscriber} className="flex-1 bg-amber-500">Add Subscriber</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List Modal */}
      {showListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Create List</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">List Name *</label>
                <input type="text" value={listForm.name} onChange={(e) => setListForm({...listForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Newsletter" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea value={listForm.description} onChange={(e) => setListForm({...listForm, description: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none" rows={3} placeholder="Describe this list..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowListModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveList} className="flex-1 bg-amber-500">Create List</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Create Campaign</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Campaign Name *</label>
                <input type="text" value={campaignForm.name} onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Spring Sale Newsletter" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email Subject *</label>
                <input type="text" value={campaignForm.subject} onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="🎉 Don't miss our spring sale!" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">From Name</label>
                  <input type="text" value={campaignForm.from_name} onChange={(e) => setCampaignForm({...campaignForm, from_name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Your Company" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Email</label>
                  <input type="email" value={campaignForm.from_email} onChange={(e) => setCampaignForm({...campaignForm, from_email: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="hello@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email Content (HTML)</label>
                <textarea value={campaignForm.html_content} onChange={(e) => setCampaignForm({...campaignForm, html_content: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white font-mono text-sm resize-none" rows={8} placeholder="<h1>Hello {{first_name}}</h1>..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowCampaignModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveCampaign} className="flex-1 bg-amber-500">Create Campaign</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">Create Template</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Template Name *</label>
                <input type="text" value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Welcome Email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Subject Line</label>
                <input type="text" value={templateForm.subject} onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white" placeholder="Welcome to our platform!" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select value={templateForm.category} onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
                  <option value="marketing">Marketing</option>
                  <option value="transactional">Transactional</option>
                  <option value="automation">Automation</option>
                  <option value="system">System</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">HTML Content</label>
                <textarea value={templateForm.html_content} onChange={(e) => setTemplateForm({...templateForm, html_content: e.target.value})} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white font-mono text-sm resize-none" rows={8} placeholder="<h1>Welcome!</h1>..." />
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => setShowTemplateModal(false)} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveTemplate} className="flex-1 bg-amber-500">Create Template</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingPanel;
